/**
 * Shared Create-rule-from-deficiency panel ( + ).
 *
 * Extracted from LearnedRulesTab.tsx so it can be reused from the case
 * detail page ("Train from this case" button, ). When `lockedCaseId`
 * is provided, the example-case picker is pre-filled + disabled so the
 * reviewer trains a rule against the case they are currently viewing.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Sparkles, Save, FilePlus2 } from 'lucide-react';
import { useToastStore } from '../../stores/toast';
import type { Case } from '../../api/cases';
import {
  generateLearnedRuleDraft,
  createLearnedRule,
  type GeneratedDraft,
  type TargetData,
  type Severity,
} from '../../api/learnedRules';

export const TARGETS: TargetData[] = ['documents', 'public_data', 'both'];
export const SEVERITIES: Severity[] = ['auto_escalate', 'high', 'medium', 'low'];

export function CaseSelect({
  value, onChange, cases, loading, id, disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  cases: Case[];
  loading: boolean;
  id?: string;
  disabled?: boolean;
}) {
  // Gap #5: when there are zero cases, guide the user instead of showing a
  // bare empty <select> that silently blocks the workflow.
  if (!loading && cases.length === 0) {
    return (
      <div className="flex flex-col gap-1 text-xs text-slate-500">
        <span>No cases yet.</span>
        <Link
          to="/cases/new"
          className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium"
        >
          <FilePlus2 size={12} /> Create a case on the Cases page
        </Link>
        <span className="text-slate-400">or seed demo data to use the learned-rules workflow.</span>
      </div>
    );
  }
  return (
    <select
      id={id}
      className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-primary-500 disabled:bg-slate-100 disabled:text-slate-500"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={loading || disabled}
    >
      <option value="">{loading ? 'Loading cases...' : 'Select a case...'}</option>
      {cases.map((c) => (
        <option key={c.id} value={c.id}>
          {c.case_number || c.id.slice(0, 8)} - {c.issuer_name || '(no issuer)'}
        </option>
      ))}
    </select>
  );
}

function DraftEditor({ draft, onChange, onCreate, creating, onCancel }: {
  draft: GeneratedDraft;
  onChange: (d: GeneratedDraft) => void;
  onCreate: () => void;
  creating: boolean;
  onCancel: () => void;
}) {
  const set = <K extends keyof GeneratedDraft>(k: K, v: GeneratedDraft[K]) =>
    onChange({ ...draft, [k]: v });
  return (
    <div className="mt-2 border-t border-slate-200 pt-3 space-y-3 bg-amber-50/50 -mx-4 -mb-4 p-4 rounded-b-lg">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Draft (editable)</p>
        <span className="text-xs text-slate-400">Review + edit before creating</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Code (LR_ prefix)</label>
          <input className="input text-sm" value={draft.code}
            onChange={(e) => set('code', e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
          <input className="input text-sm" value={draft.name}
            onChange={(e) => set('name', e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Category (1-6)</label>
          <input type="number" min={1} max={6} className="input text-sm" value={draft.category}
            onChange={(e) => set('category', parseInt(e.target.value, 10) || 1)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Target data</label>
          <select className="input text-sm" value={draft.target_data}
            onChange={(e) => set('target_data', e.target.value as TargetData)}>
            {TARGETS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Severity</label>
          <select className="input text-sm" value={draft.severity}
            onChange={(e) => set('severity', e.target.value as Severity)}>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Points (1-5)</label>
          <input type="number" min={1} max={5} className="input text-sm" value={draft.points}
            onChange={(e) => set('points', parseInt(e.target.value, 10) || 1)} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Detection prompt</label>
        <textarea className="input text-sm" rows={5} value={draft.detection_prompt}
          onChange={(e) => set('detection_prompt', e.target.value)} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Evidence fields (one per line)</label>
          <textarea className="input text-sm" rows={3}
            value={draft.evidence_fields.join('\n')}
            onChange={(e) => set('evidence_fields', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Negative examples (one per line)</label>
          <textarea className="input text-sm" rows={3}
            value={draft.negative_examples.join('\n')}
            onChange={(e) => set('negative_examples', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Rationale</label>
        <textarea className="input text-sm" rows={2} value={draft.rationale}
          onChange={(e) => set('rationale', e.target.value)} />
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onCreate} disabled={creating}
          className="btn-primary text-sm px-4 py-2 flex items-center gap-1.5 disabled:opacity-50">
          {creating ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
          {creating ? 'Creating...' : 'Create draft rule'}
        </button>
        <button onClick={onCancel} className="btn-secondary text-sm px-4 py-2">Discard</button>
      </div>
    </div>
  );
}

export function CreateRulePanel({
  cases,
  casesLoading,
  onCreated,
  lockedCaseId,
  initialDeficiency,
}: {
  cases: Case[];
  casesLoading: boolean;
  onCreated: () => void;
  /** when set, pre-fill + lock the example case picker. */
  lockedCaseId?: string;
  /** when set, pre-fill the deficiency textarea (e.g. from a chat
   * message). The reviewer can still edit it before generating. */
  initialDeficiency?: string;
}) {
  const { addToast } = useToastStore();
  const [deficiency, setDeficiency] = useState(initialDeficiency ?? '');
  const [exampleCaseId, setExampleCaseId] = useState(lockedCaseId ?? '');
  const [targetData, setTargetData] = useState<TargetData>('both');
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState<GeneratedDraft | null>(null);
  const [creating, setCreating] = useState(false);

  const handleGenerate = async () => {
    if (!deficiency.trim() || !exampleCaseId) {
      addToast('Deficiency description and example case are required', 'error');
      return;
    }
    setGenerating(true);
    try {
      const d = await generateLearnedRuleDraft(deficiency.trim(), exampleCaseId, targetData);
      setDraft(d);
      addToast('Draft generated -- review + publish', 'success');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? 'Generation failed (is the AI configured?)';
      addToast(msg, 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreate = async () => {
    if (!draft) return;
    if (!draft.code.trim() || !draft.name.trim() || !draft.detection_prompt.trim()) {
      addToast('Code, name, and detection prompt are required', 'error');
      return;
    }
    setCreating(true);
    try {
      await createLearnedRule({
        ...draft,
        source_deficiency: deficiency.trim(),
        source_case_id: exampleCaseId || null,
      });
      addToast('Learned rule created (draft) -- publish to activate', 'success');
      setDraft(null);
      setDeficiency('');
      if (!lockedCaseId) setExampleCaseId('');
      onCreated();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? 'Failed to create rule';
      addToast(msg, 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="text-primary-600" size={18} />
        <h3 className="text-sm font-semibold text-slate-800">Create rule from deficiency</h3>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Deficiency description</label>
        <textarea
          className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none"
          rows={3}
          placeholder="Describe the deficiency/pattern you observed (the AI will generalize it)..."
          value={deficiency}
          onChange={(e) => setDeficiency(e.target.value)}
        />
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex flex-col w-full sm:w-64">
          <label className="text-xs font-medium text-slate-500 mb-1">Example case</label>
          <CaseSelect
            value={exampleCaseId}
            onChange={setExampleCaseId}
            cases={cases}
            loading={casesLoading}
            disabled={!!lockedCaseId}
          />
        </div>
        <div className="flex flex-col w-full sm:w-40">
          <label className="text-xs font-medium text-slate-500 mb-1">Target data</label>
          <select
            className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-primary-500"
            value={targetData}
            onChange={(e) => setTargetData(e.target.value as TargetData)}
          >
            {TARGETS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating || !deficiency.trim() || !exampleCaseId}
          className="btn-primary text-sm px-4 py-2 flex items-center gap-1.5 disabled:opacity-50"
        >
          {generating ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
          {generating ? 'Generating...' : 'Generate draft'}
        </button>
      </div>

      {draft && (
        <DraftEditor
          draft={draft}
          onChange={setDraft}
          onCreate={handleCreate}
          creating={creating}
          onCancel={() => setDraft(null)}
        />
      )}
    </div>
  );
}